'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, Navigate, NavLink, useLocation, useNavigate } from '@/lib/router'
import { api, DATA_CHANGED } from '../api/client'
import type { ProjectListItem } from '../api/types'
import { useAuth } from '../auth/useAuth'

const NAV = [
  { to: '/', label: 'Dashboard' },
  { to: '/pipeline', label: 'Pipeline' },
  { to: '/focus', label: '30-Day Focus' },
  { to: '/overdue', label: 'Overdue' },
  { to: '/documents', label: 'Documents' },
  { to: '/report', label: 'Report' },
  { to: '/stakeholders', label: 'Stakeholders' },
  { to: '/departments', label: 'Departments' },
]

/**
 * Screen 2 – the dark top bar shown on every signed-in page.
 * Signed-out visitors are sent to /signin first.
 */
export default function Layout({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth()
  const location = useLocation()

  if (!ready) return null
  if (!user) return <Navigate to="/signin" replace state={{ from: location.pathname }} />
  // Someone with a temporary password must choose their own before using the app.
  if (user.mustChangePassword) return <Navigate to="/change-password" replace />

  return (
    <>
      <TopBar />
      <StackWideTables />
      {children}
    </>
  )
}

function TopBar() {
  const overdue = useOverdueCount()
  const { pathname } = useLocation()
  // Phones: the page links fold away behind a Menu button.
  const [menuOpen, setMenuOpen] = useState(false)

  // Close the menu after moving to another page.
  useEffect(() => setMenuOpen(false), [pathname])

  return (
    <header className={menuOpen ? 'topbar menu-open' : 'topbar'}>
      <button
        type="button"
        className="nav-toggle"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-expanded={menuOpen}
        aria-controls="main-nav"
      >
        <span aria-hidden="true">{menuOpen ? '✕' : '☰'}</span> Menu
        {!menuOpen && overdue > 0 && <span className="count-badge" title={`${overdue} overdue`}>{overdue}</span>}
      </button>

      <Link to="/" className="brand">
        GCRM <span className="dot">·</span> <span className="brand-sub">Pipeline Workspace</span>
      </Link>

      <nav className="nav" id="main-nav">
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to === '/'} className="nav-link">
            {item.label}
            {item.to === '/overdue' && overdue > 0 && <span className="count-badge">{overdue}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="topbar-actions">
        <Link to="/projects/new" className="btn btn-outline-light" aria-label="New project">
          + New<span className="hide-on-phone"> project</span>
        </Link>
        <UserMenu />
      </div>
    </header>
  )
}

/** Up to this width (tablets, small laptops, zoomed windows) wide tables may turn into cards. */
const STACK_TABLES_BELOW = '(max-width: 1100px)'

/** The width available to an element inside its parent (the parent's width minus its padding). */
function roomFor(el: HTMLElement): number {
  const parent = el.parentElement
  if (!parent) return window.innerWidth
  const style = getComputedStyle(parent)
  return parent.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
}

/**
 * On phones, tablets and narrow windows, a table too wide for its space becomes a stack of cards: one card
 * per row, each value labelled with its column heading (labels come from the table's own <th>, so screens
 * need no changes). Tables that already fit stay as tables, so no column is ever hidden off to the side.
 */
function StackWideTables() {
  useEffect(() => {
    const phone = window.matchMedia(STACK_TABLES_BELOW)

    const update = () => {
      for (const table of document.querySelectorAll<HTMLTableElement>('table.table')) {
        const headings = [...table.querySelectorAll('thead th')].map((th) => th.textContent?.trim() ?? '')
        for (const row of table.querySelectorAll('tbody tr')) {
          let column = 0
          for (const cell of row.children as HTMLCollectionOf<HTMLTableCellElement>) {
            const label = cell.colSpan > 1 ? '' : headings[column] ?? ''
            if (label) cell.setAttribute('data-label', label)
            else cell.removeAttribute('data-label')
            column += cell.colSpan || 1
          }
        }
        // Measure as a normal table; stack it only if it would not fit inside its container.
        table.classList.remove('stacked')
        if (phone.matches && table.scrollWidth > roomFor(table) + 1) table.classList.add('stacked')
      }
    }

    let queued = 0
    const schedule = () => {
      cancelAnimationFrame(queued)
      queued = requestAnimationFrame(update)
    }

    schedule()
    // Re-check when rows load or change, and when the phone is turned.
    const observer = new MutationObserver(schedule)
    observer.observe(document.body, { childList: true, subtree: true, characterData: true })
    phone.addEventListener('change', schedule)
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(queued)
      observer.disconnect()
      phone.removeEventListener('change', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [])

  return null
}

/** Live count for the Overdue badge; refreshed when the page changes or anything is saved. */
function useOverdueCount() {
  const [count, setCount] = useState(0)
  const location = useLocation()

  useEffect(() => {
    const refresh = () =>
      api<ProjectListItem[]>('/projects')
        .then((projects) => setCount(projects.filter((p) => p.isOverdue).length))
        .catch(() => setCount(0))

    refresh()
    window.addEventListener(DATA_CHANGED, refresh)
    return () => window.removeEventListener(DATA_CHANGED, refresh)
  }, [location.pathname])

  return count
}

function UserMenu() {
  const { user, signOut, demoTools } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close the menu when clicking anywhere else.
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  if (!user) return null

  /** Replaces all projects, people and documents after asking first. */
  const replaceData = async (kind: 'reset' | 'demo') => {
    const question =
      kind === 'reset'
        ? 'Reset to the original 18 sample projects?\n\nThis replaces ALL projects, stakeholders, departments, activities and attachments for everyone. It cannot be undone.'
        : 'Load the large demo data set (50 projects, 200 people)?\n\nThis replaces ALL projects, stakeholders, departments, activities and attachments for everyone. It cannot be undone.'
    if (!window.confirm(question)) return

    setBusy(true)
    try {
      await api(kind === 'reset' ? '/admin/reset-sample' : '/admin/load-demo', 'POST')
      window.location.assign('/') // reload everything from scratch on the Dashboard
    } catch (err) {
      setBusy(false)
      alert(`That did not work: ${(err as Error).message}`)
    }
  }

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className="avatar"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={user.name}
      >
        {user.initials}
      </button>
      {open && (
        <div className="menu" role="menu">
          <strong>{user.name}</strong>
          <small className="muted">
            {user.email}
            {user.isAdmin && ' · Admin'}
          </small>
          <div className="menu-items">
            <Link to="/change-password" className="menu-link" onClick={() => setOpen(false)}>Change password</Link>
            {user.isAdmin && (
              <Link to="/team" className="menu-link" onClick={() => setOpen(false)}>Manage team</Link>
            )}
            {user.isAdmin && demoTools && (
              <>
                <button className="menu-link" disabled={busy} onClick={() => replaceData('reset')}>
                  Reset sample data
                </button>
                <button className="menu-link" disabled={busy} onClick={() => replaceData('demo')}>
                  Load large demo data
                </button>
              </>
            )}
          </div>
          {busy && <small className="muted">Working…</small>}
          <button
            className="btn btn-small"
            onClick={async () => {
              await signOut()
              navigate('/signin')
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
